import React, { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  Button,
  TextField,
  Pagination,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    minWidth: "800px",
  },
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const ProductsFilters = ({
  productsList,
  open,
  handleProductsClose,
  handleProductsApply,
  selectedMaterials,
  setSelectedMaterials,
  Products,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortedMaterials, setSortedMaterials] = useState([]);
  const itemsPerPage = 50;

  // Initialize selectedMaterials with Products
  useEffect(() => {
    if (Products && Products.length > 0) {
      setSelectedMaterials((prev) => [...new Set([...prev, ...Products])]);
    }
  }, [Products, setSelectedMaterials]);

  // Sort productsList to bring Products to the top
  useEffect(() => {
    if (productsList) {
      const sorted = [...productsList].sort((a, b) => {
        if (Products.includes(a) && !Products.includes(b)) {
          return -1;
        }
        if (!Products.includes(a) && Products.includes(b)) {
          return 1;
        }
        return 0;
      });
      setSortedMaterials(sorted);
    }
  }, [productsList, Products]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleCheckboxChange = (id) => {
    setSelectedMaterials((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handlePageChange = (_event, value) => {
    setCurrentPage(value);
  };

  const handleClose = () => {
    handleProductsClose();
  };

  const handleApply = () => {
    handleProductsApply(selectedMaterials);
  };

  // Filter materials based on search term
  const filteredMaterials = sortedMaterials?.filter((material) =>
    material.includes(searchTerm?.toLowerCase()),
  );

  // Paginate the filtered materials
  const paginatedMaterials = filteredMaterials.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const pageCount = Math.ceil(filteredMaterials.length / itemsPerPage);

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          fontSize: 16,
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        id="customized-dialog-title"
      >
        <span
          style={{ fontSize: "18px", color: "#222", fontFamily: "Poppins" }}
        >
          Select Products
        </span>
        <span style={{ display: "flex", alignItems: "center" }}>
          <Typography
            style={{
              color: " #929497",
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              margin: "0px 10px",
            }}
          >
            {selectedMaterials.length > 0
              ? `${selectedMaterials.length} Selected`
              : ""}
          </Typography>
          <TextField
            hiddenLabel
            id="search-input"
            placeholder="search"
            value={searchTerm}
            onChange={handleSearch}
            variant="outlined"
            size="small"
            sx={{
              width: "400px",
              borderRadius: "8px",
              border: "1px solid var(--Neutral-Colors-Off-White, #E6E7E8)",
              "& .MuiInputBase-input": {
                height: "15px",
                fontSize: "12px",
              },
            }}
          />
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              marginLeft: "8px",
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </span>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexWrap: "wrap" }}>
        <List
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
          }}
        >
          {paginatedMaterials?.map((material) => (
            <ListItem
              key={material}
              sx={{ width: "20%", display: "flex", alignItems: "center" }}
              disableGutters
            >
              <ListItemIcon style={{ minWidth: "0px !important" }}>
                <Checkbox
                  checked={selectedMaterials.includes(material)}
                  onChange={() => handleCheckboxChange(material)}
                  sx={{
                    color: "#0067A1",
                    "&.Mui-checked": {
                      color: "#0067A1",
                    },
                  }}
                />
              </ListItemIcon>
              <ListItemText
                style={{
                  "& MuiTypography-root ": {
                    color: "#333",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "10px !important",
                    fontWeight: "400",
                  },
                }}
                primary={`${material}`}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions
        sx={{ mr: 2, p: 4 }}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Pagination
          count={pageCount}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          sx={{
            marginTop: 2,
            marginBottom: 2,
            "& .MuiPaginationItem-root": {
              backgroundColor: "#FFF",
              borderRadius: "4px",
              border: "1px solid var(--Neutrals-Grey-light, #E6E7E8)",
              color: "#59595A",
              "&.Mui-selected": {
                backgroundColor: "#2B5692",
                color: "#FFF",
              },
              "&:hover": {
                backgroundColor: "#FFF",
              },
            },
          }}
        />
        <Typography
          variant="body2"
          style={{
            marginRight: "auto",
            color: "#929497",
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
          }}
        >
          {`Showing ${paginatedMaterials?.length} results out of ${filteredMaterials.length}`}
        </Typography>
        <div>
          <Button
            variant="contained"
            onClick={handleClose}
            style={{
              background: "#FFF",
              textTransform: "none",
              color: "black",
              margin: "0px 3px",
              fontSize: "14px",
              borderRadius: "4px",
              border: "1px solid var(--Neutral-Colors-Charcoal-Grey, #404041)",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            style={{
              borderRadius: "4px",
              background: "#2B5692",
              textTransform: "none",
              margin: "0px 3px",
              color: "#FFF",
              fontSize: "14px",
            }}
          >
            Apply
          </Button>
        </div>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default ProductsFilters;
